<template>
  <div
    :class="{ 'form-popup-overlay': true, 'is-visible': isVisible, 'px-5': true }"
    @click="hide">
    <div
      :class="{ 'form-popup': true, 'px-5': true, 'pb-4': true, 'pt-4': true, 'is-loading': loading }"
      @click.stop>
      <h3 class="text-center">
        {{ popupTitle }}
      </h3>

      <validation-observer ref="visa-form" tag="form">
        <d-row>
          <div class="form-group w-100">
            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <label for="name">Name:</label>
              <d-input
                v-model="data.name"
                :state="errors.length ? 'invalid' : null" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </d-row>

        <d-row>
          <div class="form-group w-100">
            <label for="country_id">Country:</label>

            <validation-provider
              rules="required"
              name="country_id"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="countriesOptions"
                  v-model="data.country_id"
                  :reduce="country => country.id"
                  placeholder="Select a country"
                  label="text">
                  <template slot="no-options">
                    There are no countries for the provided phrase&hellip;
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </d-row>

        <d-row>
          <div class="form-group w-100">
            <validation-provider
              rules="required"
              name="valid_for"
              v-slot="{ errors }">
              <label for="valid_for">Valid for:</label>
              <d-datepicker
                v-model="data.valid_for"
                format="dMMMyy"
                :class="{ 'is-invalid': errors.length }"
                placeholder="Select a date" />

              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </d-row>

        <d-row>
          <div class="form-group w-100">
            <validation-provider
              rules="required"
              name="end_date"
              v-slot="{ errors }">
              <label for="end_date">End date:</label>
              <d-datepicker
                v-model="data.end_date"
                format="dMMMyy"
                :class="{ 'is-invalid': errors.length }"
                placeholder="Select a date" />

              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </d-row>
      </validation-observer>

      <d-row>
        <div class="text-center w-100">
          <d-button
            @click.prevent="validateForm"
            theme="success"
            class="mr-5">
            Save
          </d-button>

          <d-button
            @click.prevent="cancel"
            outline
            theme="secondary">
            Cancel
          </d-button>
        </div>
      </d-row>
    </div>
  </div>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import DateUtils from './../../utils/DateUtils.js';
import FormUtils from './../../utils/FormUtils';

export default {
  name: 'VisaPopup',
  computed: {
    popupTitle () {
      if (this.mode === 'add') {
        return 'Add visa';
      }

      return 'Edit visa';
    },
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    }
  },
  data () {
    return {
      isVisible: false,
      mode: 'add',
      loading: false,
      countries: [],
      loadedCountries: false,
      loadError: false,
      data: {
        id: false,
        country_id: null,
        valid_for: null,
        name: '',
        end_date: null,
        reference_type: null,
        reference_id: null
      }
    };
  },
  mounted () {
    this.$bus.$on('show-visa-popup', this.show);
    this.$bus.$on('countries-list-reload', this.loadAdditionalData);
    this.loadAdditionalData();
  },
  methods: {
    show (mode, referenceType = false, referenceId = false, documentId = false) {
      this.mode = mode;

      if (mode === 'add') {
        this.loading = false;
        this.data = {
          id: false,
          country_id: null,
          valid_for: null,
          end_date: null,
          name: '',
          reference_type: referenceType,
          reference_id: referenceId
        };
      } else {
        this.loading = true;
        this.loadVisaData(documentId);
      }

      setTimeout(() => {
        this.$refs['visa-form'].reset();
        this.isVisible = true;
      }, 100);
    },
    loadVisaData (visaId) {
      FormUtils.loadItemData(this, {
        id: visaId,
        endpointBase: '/visas/item/',
        instanceFields: ['id', 'name', 'country_id', 'valid_for', 'end_date', 'reference_type', 'reference_id'],
        loadedKey: 'loaded',
        successAction: () => {
          this.loading = false;
          this.data = DateUtils.groupConversionStringToDate(this.data, ['valid_for', 'end_date']);
          this.$refs['visa-form'].reset();
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/countries/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['visa-form'], this.submitForm);
    },
    submitForm () {
      this.loading = true;
      let endpointUrl = '/visas/store';

      if (this.mode === 'edit') {
        endpointUrl = '/visas/update/' + this.data.id;
      }

      let formDataToSend = this.prepareFormData();

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.mode === 'add' ? 'Visa added' : 'Visa saved',
        successText: '',
        errorTitle: 'An error occurred',
        errorText: 'The system was unable to save the visa. Please try again.',
        successAction: () => {
          this.loading = false;
          this.isVisible = false;
          this.$bus.$emit('visas-list-reload');
        }
      });
    },
    cancel () {
      this.isVisible = false;
      this.loading = false;
    },
    hide () {
      this.isVisible = false;
      this.loading = false;
    },
    prepareFormData () {
      this.data = DateUtils.groupConversionDateToString(this.data, ['valid_for', 'end_date']);

      let dataToSend = {
        name: this.data.name,
        country_id: this.data.country_id,
        valid_for: this.data.valid_for,
        end_date: this.data.end_date,
        reference_type: this.data.reference_type,
        reference_id: this.data.reference_id
      };

      return dataToSend;
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-visa-popup', this.show);
    this.$bus.$off('countries-list-reload', this.loadAdditionalData);
  }
}
</script>
